import { useEffect, useRef, useState } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  OutlinedInput,
  Select,
  SelectChangeEvent,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import { DateTimePicker } from "@mui/x-date-pickers/DateTimePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { ru } from "date-fns/locale";
import {
  BenefitCategory,
  BenefitContact,
  BenefitContactType,
  BenefitDiscountType,
  BenefitService,
  BENEFIT_CONTACT_TYPE_LABELS,
  BENEFIT_CONTACT_TYPE_OPTIONS,
  BENEFIT_DISCOUNT_TYPE_LABELS,
  PartnerBenefitServiceBody,
  useUploadBenefitServicePhotoMutation,
} from "../../../entities/admin-benefits";
import { City } from "../../../entities/admin-city";
import { FileUploadInfo } from "../../../shared/ui/file-upload-info";
import {
  FILE_SIZE_LIMIT_BYTES,
  getFileSizeLimitError,
} from "../../../shared/constants/file-upload-limits";

const ACCEPTED_PHOTO_TYPES = ["image/jpeg", "image/png"];

interface BenefitsPartnerFormDialogProps {
  open: boolean;
  service: BenefitService | null;
  categories: BenefitCategory[];
  cities: City[];
  isSubmitting: boolean;
  onClose: () => void;
  onSubmit: (body: PartnerBenefitServiceBody) => void;
}

const emptyContact = (): BenefitContact => ({
  type: "telegram",
  value: "",
});

export const BenefitsPartnerFormDialog = ({
  open,
  service,
  categories,
  cities,
  isSubmitting,
  onClose,
  onSubmit,
}: BenefitsPartnerFormDialogProps) => {
  const [partnerName, setPartnerName] = useState("");
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [categoryId, setCategoryId] = useState<number | "">("");
  const [discountType, setDiscountType] = useState<BenefitDiscountType>("none");
  const [discountValue, setDiscountValue] = useState("");
  const [promoCode, setPromoCode] = useState("");
  const [promoStartsAt, setPromoStartsAt] = useState<Date | null>(null);
  const [promoExpiresAt, setPromoExpiresAt] = useState<Date | null>(null);
  const [expiresAt, setExpiresAt] = useState<Date | null>(null);
  const [photoFileId, setPhotoFileId] = useState<string | undefined>();
  const [photoPreviewUrl, setPhotoPreviewUrl] = useState<string | undefined>();
  const [contacts, setContacts] = useState<BenefitContact[]>([emptyContact()]);
  const [cityIds, setCityIds] = useState<number[]>([]);

  const fileInputRef = useRef<HTMLInputElement>(null);
  const [uploadPhoto, { isLoading: isUploadingPhoto }] =
    useUploadBenefitServicePhotoMutation();

  useEffect(() => {
    if (!open) return;
    if (service) {
      setPartnerName(service.partnerName ?? "");
      setTitle(service.title);
      setDescription(service.description);
      setCategoryId(service.categoryId);
      setDiscountType(service.discountType);
      setDiscountValue(
        service.discountValue ? String(service.discountValue) : "",
      );
      setPromoCode(service.promoCode ?? "");
      setPromoStartsAt(
        service.promoStartsAt ? new Date(service.promoStartsAt) : null,
      );
      setPromoExpiresAt(
        service.promoExpiresAt ? new Date(service.promoExpiresAt) : null,
      );
      setExpiresAt(service.expiresAt ? new Date(service.expiresAt) : null);
      setPhotoFileId(undefined);
      setPhotoPreviewUrl(service.photoUrl);
      setContacts(
        service.contacts?.length ? [...service.contacts] : [emptyContact()],
      );
      setCityIds(service.cityIds ?? []);
    } else {
      setPartnerName("");
      setTitle("");
      setDescription("");
      setCategoryId("");
      setDiscountType("none");
      setDiscountValue("");
      setPromoCode("");
      setPromoStartsAt(null);
      setPromoExpiresAt(null);
      setExpiresAt(null);
      setPhotoFileId(undefined);
      setPhotoPreviewUrl(undefined);
      setContacts([emptyContact()]);
      setCityIds([]);
    }
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  }, [open, service]);

  const handlePhotoChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (!ACCEPTED_PHOTO_TYPES.includes(file.type)) {
      alert("Допустимы только JPEG и PNG");
      return;
    }
    const sizeError = getFileSizeLimitError(file, FILE_SIZE_LIMIT_BYTES.DEFAULT);
    if (sizeError) {
      alert(sizeError);
      return;
    }

    const formData = new FormData();
    formData.append("file", file);

    try {
      const result = await uploadPhoto(formData).unwrap();
      setPhotoFileId(result.fileId);
      setPhotoPreviewUrl(result.url);
    } catch {
      alert("Не удалось загрузить фото");
    }
  };

  const handleCityChange = (event: SelectChangeEvent<number[]>) => {
    const value = event.target.value;
    setCityIds(
      typeof value === "string" ? value.split(",").map(Number) : value,
    );
  };

  const buildBody = (): PartnerBenefitServiceBody | null => {
    if (!partnerName.trim() || !title.trim() || !description.trim()) {
      alert("Заполните название партнёра, заголовок и описание");
      return null;
    }
    if (categoryId === "") {
      alert("Выберите категорию");
      return null;
    }

    const body: PartnerBenefitServiceBody = {
      partnerName: partnerName.trim(),
      title: title.trim(),
      description: description.trim(),
      categoryId: Number(categoryId),
      discountType,
      contacts: contacts.filter((c) => c.value.trim()),
      cityIds,
    };

    if (discountType === "percent" || discountType === "fixedAmount") {
      const value = parseFloat(discountValue);
      if (Number.isNaN(value)) {
        alert("Укажите значение скидки");
        return null;
      }
      body.discountValue = value;
    }

    if (promoCode.trim()) {
      body.promoCode = promoCode.trim();
      if (promoStartsAt) body.promoStartsAt = promoStartsAt.toISOString();
      if (promoExpiresAt) body.promoExpiresAt = promoExpiresAt.toISOString();
    }

    if (expiresAt) {
      body.expiresAt = expiresAt.toISOString();
    }

    if (photoFileId) {
      body.photoFileId = photoFileId;
    }

    return body;
  };

  const handleSubmit = () => {
    const body = buildBody();
    if (body) onSubmit(body);
  };

  const showDiscountValue =
    discountType === "percent" || discountType === "fixedAmount";

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={ru}>
      <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
        <DialogTitle>
          {service ? "Редактировать партнёрскую услугу" : "Создать партнёрскую услугу"}
        </DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              label="Название партнёра"
              required
              fullWidth
              value={partnerName}
              onChange={(e) => setPartnerName(e.target.value)}
            />
            <TextField
              label="Название услуги"
              required
              fullWidth
              value={title}
              onChange={(e) => setTitle(e.target.value)}
            />
            <TextField
              label="Описание"
              required
              fullWidth
              multiline
              minRows={3}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
            />

            <FormControl fullWidth required>
              <InputLabel>Категория</InputLabel>
              <Select
                value={categoryId}
                label="Категория"
                onChange={(e) => setCategoryId(e.target.value as number)}
              >
                {categories.map((cat) => (
                  <MenuItem key={cat.id} value={cat.id}>
                    {cat.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
              <FormControl fullWidth>
                <InputLabel>Тип скидки</InputLabel>
                <Select
                  value={discountType}
                  label="Тип скидки"
                  onChange={(e) =>
                    setDiscountType(e.target.value as BenefitDiscountType)
                  }
                >
                  {(
                    Object.keys(BENEFIT_DISCOUNT_TYPE_LABELS) as BenefitDiscountType[]
                  ).map((type) => (
                    <MenuItem key={type} value={type}>
                      {BENEFIT_DISCOUNT_TYPE_LABELS[type]}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              {showDiscountValue && (
                <TextField
                  label={
                    discountType === "percent"
                      ? "Процент скидки"
                      : "Сумма скидки"
                  }
                  type="number"
                  fullWidth
                  value={discountValue}
                  onChange={(e) => setDiscountValue(e.target.value)}
                />
              )}
            </Stack>

            <TextField
              label="Промокод"
              fullWidth
              value={promoCode}
              onChange={(e) => setPromoCode(e.target.value)}
            />
            <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
              <DateTimePicker
                label="Промо с"
                value={promoStartsAt}
                onChange={setPromoStartsAt}
                slotProps={{ textField: { fullWidth: true } }}
              />
              <DateTimePicker
                label="Промо до"
                value={promoExpiresAt}
                onChange={setPromoExpiresAt}
                slotProps={{ textField: { fullWidth: true } }}
              />
            </Stack>

            <DateTimePicker
              label="Срок действия услуги"
              value={expiresAt}
              onChange={setExpiresAt}
              slotProps={{
                textField: {
                  fullWidth: true,
                  helperText: "Если не указан — 1 год от создания",
                },
              }}
            />

            <Box>
              <Typography variant="subtitle2" gutterBottom>
                Фото (512×512, JPEG/PNG, до 5 МБ)
              </Typography>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/jpeg,image/png"
                style={{ display: "none" }}
                onChange={handlePhotoChange}
              />
              <Button
                variant="outlined"
                component="span"
                startIcon={<CloudUploadIcon />}
                onClick={() => fileInputRef.current?.click()}
                disabled={isUploadingPhoto}
              >
                {isUploadingPhoto ? "Загрузка..." : "Загрузить фото"}
              </Button>
              <FileUploadInfo
                type="image"
                recommendedSize="512×512 px"
              />
              {photoPreviewUrl && (
                <Box
                  component="img"
                  src={photoPreviewUrl}
                  alt="Превью"
                  sx={{
                    mt: 1,
                    width: 128,
                    height: 128,
                    objectFit: "cover",
                    borderRadius: 1,
                  }}
                />
              )}
            </Box>

            <FormControl fullWidth>
              <InputLabel>Города</InputLabel>
              <Select
                multiple
                value={cityIds}
                onChange={handleCityChange}
                input={<OutlinedInput label="Города" />}
                renderValue={(selected) =>
                  selected
                    .map((id) => cities.find((c) => c.id === id)?.name ?? id)
                    .join(", ")
                }
              >
                {cities.map((city) => (
                  <MenuItem key={city.id} value={city.id}>
                    {city.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <Typography variant="subtitle2">Контакты</Typography>
            {contacts.map((contact, index) => (
              <Stack key={index} direction="row" spacing={1}>
                <FormControl sx={{ minWidth: 140 }}>
                  <InputLabel>Тип</InputLabel>
                  <Select
                    value={contact.type}
                    label="Тип"
                    onChange={(e) => {
                      const next = [...contacts];
                      next[index] = {
                        ...contact,
                        type: e.target.value as BenefitContactType,
                      };
                      setContacts(next);
                    }}
                  >
                    {(BENEFIT_CONTACT_TYPE_OPTIONS.includes(contact.type)
                      ? BENEFIT_CONTACT_TYPE_OPTIONS
                      : [...BENEFIT_CONTACT_TYPE_OPTIONS, contact.type]
                    ).map((type) => (
                      <MenuItem key={type} value={type}>
                        {BENEFIT_CONTACT_TYPE_LABELS[type]}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <TextField
                  fullWidth
                  label="Значение"
                  value={contact.value}
                  onChange={(e) => {
                    const next = [...contacts];
                    next[index] = { ...contact, value: e.target.value };
                    setContacts(next);
                  }}
                />
                <IconButton
                  color="error"
                  onClick={() =>
                    setContacts(contacts.filter((_, i) => i !== index))
                  }
                  disabled={contacts.length <= 1}
                >
                  <DeleteIcon />
                </IconButton>
              </Stack>
            ))}
            <Button
              size="small"
              startIcon={<AddIcon />}
              onClick={() => setContacts([...contacts, emptyContact()])}
            >
              Добавить контакт
            </Button>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} disabled={isSubmitting}>
            Отмена
          </Button>
          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={isSubmitting || isUploadingPhoto}
          >
            {isSubmitting ? "Сохранение..." : "Сохранить"}
          </Button>
        </DialogActions>
      </Dialog>
    </LocalizationProvider>
  );
};
